import React, {useMemo} from "react";
import {T, useTranslate} from "@tolgee/react";
import FlexBox from "view/components/Layout/FlexBox";
import {Box, CircularProgress, Typography} from "@material-ui/core";
import ShowView from "view/SignUp/SignUpToken/components/ShowView";
import StepHeader from "view/apps/Organiser/pages/Show/Settings/TaskCertificates/CreateUpdateSteps/Components/StepHeader";
import {
  useFormActionsContext,
  useFormStateContext,
} from "view/SignUp/SignUpToken/context";
import GoogleSignInButton from "view/components/GoogleSignInButton";
import TwoFactorChallenge from "view/components/TwoFactorChallenge";
import FlexRow from "view/components/Layout/FlexRow";
import useRedirect from "hooks/useRedirect";
import stepToComponent from "view/SignUp/SignUpToken/utils/mapStepToComponent";
import {FFairPrimaryIconGreyLogo} from "view/images/FFairLogo";
import {generatePath, useParams} from "react-router-dom";
import {jwtDecode} from "jwt-decode";
import {SHOW_EXHIBITOR_QUICK_CART_PATH} from "view/routes";
import {quickOrderIdParamName} from "tools/routing";
import useStyles from "./styles";

type QuickOrderTokenPayload = {
  showId?: string;
  exhibitorPairId?: string;
  quickOrderId?: string;
};

const SignUpToken = () => {
  const classes = useStyles();
  const {t} = useTranslate();
  const {
    activeStep,
    isShowLoading,
    show,
    googleIdentity,
    googleTwoFactor,
    isGoogleBusy,
  } = useFormStateContext();
  const {
    startGoogleSignUp,
    completeGoogleTwoFactor,
    cancelGoogleTwoFactor,
  } = useFormActionsContext();
  const {uid: token} = useParams();

  const steps = [
    t("global.sign_up.add_credentials"),
    t("global.sign_up.provide_details"),
  ];

  const quickOrderRedirectPath = useMemo(() => {
    if (!token) return undefined;
    try {
      const decoded = jwtDecode<QuickOrderTokenPayload>(token);
      if (decoded.showId && decoded.exhibitorPairId && decoded.quickOrderId) {
        return `${generatePath(SHOW_EXHIBITOR_QUICK_CART_PATH, {
          showId: decoded.showId,
          exhibitorPairId: decoded.exhibitorPairId,
        })}?${quickOrderIdParamName}=${decoded.quickOrderId}`;
      }
    } catch {
      console.warn("Invalid token: ", token);
    }
    return undefined;
  }, [token]);

  useRedirect(quickOrderRedirectPath);

  const shouldShowBottomLogo = activeStep === 0 || activeStep === 1;

  const CurrentStep = stepToComponent[activeStep];

  // The account already existed and asked for a second factor. Answering it
  // here keeps the person on the page they started on, instead of sending them
  // to the login page to begin again.
  if (googleTwoFactor) {
    return (
      <TwoFactorChallenge
        pendingToken={googleTwoFactor.pendingToken}
        method={googleTwoFactor.method}
        methods={googleTwoFactor.methods}
        onComplete={completeGoogleTwoFactor}
        onCancel={cancelGoogleTwoFactor}
      />
    );
  }

  return (
    <>
      <Box
        className={classes.wrapper}
        style={{
          minHeight: shouldShowBottomLogo ? "calc(100vh - 64px)" : "100vh",
        }}
      >
        {isShowLoading && <CircularProgress size={30} />}
        {!isShowLoading && !show && (
          <Typography>
            <T keyName="global.sign_up.event_offline" />
          </Typography>
        )}
        {!!show && (
          <FlexBox column gap={3} className={classes.root}>
            <ShowView />
            {activeStep === 1 && (
              <FlexRow fullWidth>
                <Typography variant="h2" className={classes.header}>
                  <T keyName="global.sign_up.provide_details" />
                </Typography>
              </FlexRow>
            )}
            {activeStep === 0 && (
              <Box width="100%">
                <GoogleSignInButton
                  onClick={startGoogleSignUp}
                  labelKey={
                    googleIdentity
                      ? "global.sign_up.change_google_account"
                      : "global.sign_up.sign_up_with_google"
                  }
                  isLoading={isGoogleBusy}
                />
              </Box>
            )}
            {activeStep !== 2 && (
              <FlexBox gap={1.5} alignCenter width="100%">
                {steps.map((step, index, arr) => (
                  <StepHeader
                    key={step}
                    text={step}
                    isChosen={index === activeStep}
                    isLast={index === arr.length - 1}
                    isComplete={activeStep > index}
                    orderNumber={index + 1}
                    textClassName={classes.headerText}
                  />
                ))}
              </FlexBox>
            )}
            <Box width="100%" maxWidth="100%">
              <CurrentStep />
            </Box>
          </FlexBox>
        )}
      </Box>
      {activeStep === 0 && (
        <FlexBox className={classes.bottomLogoRow} alignCenter gap={1}>
          <FFairPrimaryIconGreyLogo width={24} height={24} />
          <span className={classes.copyrightClaim}>
            Copyright © FFAIR Ltd {new Date().getFullYear()}.
          </span>
        </FlexBox>
      )}
    </>
  );
};

export default SignUpToken;
